import { z } from "zod";
import { modeRobotSchema } from "./robot";

/**
 * Le contrat de la regie de la diffusion : l'ecran « Reseaux » (les sept reseaux, branches ou non,
 * et l'autorisation de YouTube) et l'ecran « Suivi » (les envois de l'horloge, et leurs boutons
 * « Relancer », « C'est parti » et « Retirer »). Il est partage entre les routes `/api/diffusion/reseaux`,
 * `/api/diffusion/envois` du backend (`packages/backend/src/app/reseaux/routes.ts`) et la page
 * `/regie/diffusion` du frontend (`diffusion.tsx`). Les deux cotes le lisent par leur pont
 * `src/app/contract.ts`.
 *
 * Il vit ici, et non dans `contract.ts` de ce paquet, parce que `src/contract.ts` appartient au
 * template : l'app possede `src/app/**`.
 *
 * Rien de ce qu'un reseau a ecrit n'y figure : les causes sont des codes courts ecrits par l'app, et la
 * regie a un texte pour chacun (`diffusion-texts.ts`).
 */

/** Les sept reseaux de la diffusion, dans l'ordre de l'ecran « Reseaux ». Ceux de la table des envois. */
export const RESEAUX = [
  "youtube",
  "bluesky",
  "instagram",
  "facebook",
  "threads",
  "tiktok",
  "x",
] as const;
export type Reseau = (typeof RESEAUX)[number];
export const reseauSchema = z.enum(RESEAUX);

/** Les reseaux qui ne publient que de la video : un texte seul n'y a pas de sens (l'essai d'envoi l'exige du clip). */
export const RESEAUX_VIDEO_SEULE: readonly Reseau[] = ["youtube", "facebook", "instagram"];

/**
 * La voie d'un reseau : `api` quand Castellan lui parle lui-meme, `navigateur` quand c'est le robot du
 * PC qui poste dans un vrai navigateur (TikTok et X).
 */
export const VOIES = ["api", "navigateur"] as const;
export type Voie = (typeof VOIES)[number];
export const voieSchema = z.enum(VOIES);

/**
 * Ce que la regie sait d'un reseau, tel que `GET /api/diffusion/reseaux` le rend : un objet par
 * reseau, jamais un jeton.
 *
 * - `branche` : le reseau peut publier (variables posees, compte autorise, jetons valides) ;
 * - `compte` : le compte sur lequel on publie (le nom de la chaine, l'identifiant Bluesky), ou `null` ;
 * - `raison` : pourquoi il n'est pas branche, une phrase ecrite par l'app (ou le nom des variables qui
 *   manquent), `null` quand il l'est ;
 * - `expireLe` : quand l'autorisation expire (date ISO 8601), ou `null` ;
 * - `voie` : comment Castellan lui parle ;
 * - `lecture` : ce que dit la lecture des chiffres du reseau (TikTok seulement dans ce lot : l'API le lit, le robot
 *   publiera), ou `null` pour un reseau qui n'a pas de lecture a part.
 */
export const lectureSchema = z.object({
  autorisee: z.boolean(),
  compte: z.string().nullable(),
  raison: z.string().nullable(),
  expireLe: z.string().nullable(),
});
export type Lecture = z.infer<typeof lectureSchema>;

/**
 * Ce que la regie sait du robot de TikTok (lot M3), sur la ligne TikTok seulement : le mode reglé, si le robot est
 * disponible (vu il y a moins de 5 minutes par l'agent de l'atelier), quand il a ete vu, quand le dernier passage a eu
 * lieu, quand est le prochain passage quotidien (heure de Paris des reglages ; aucun en mode `arret`), et la fenetre de programmation que le
 * robot a mesuree dans TikTok Studio. Les dates sont en ISO 8601 UTC ; `null` quand on ne sait pas.
 */
export const robotLigneSchema = z.object({
  mode: modeRobotSchema,
  disponible: z.boolean(),
  vuLe: z.string().nullable(),
  dernierPassage: z.string().nullable(),
  prochainPassage: z.string().nullable(),
  /**
   * Vrai quand l'heure du passage d'aujourd'hui est venue, que le passage n'a pas eu lieu, que le mode n'est pas `arret` et que
   * le robot est absent : le passage partira des que le robot est revu, il n'attend pas demain. Alors `prochainPassage` est
   * `null`, comme en mode `arret` (aucun passage n'est annonce).
   */
  passageEnAttente: z.boolean(),
  fenetreJours: z.number().int().nullable(),
});
export type RobotLigne = z.infer<typeof robotLigneSchema>;

export const ligneReseauSchema = z.object({
  reseau: reseauSchema,
  branche: z.boolean(),
  compte: z.string().nullable(),
  raison: z.string().nullable(),
  expireLe: z.string().nullable(),
  voie: voieSchema,
  lecture: lectureSchema.nullable(),
  /** L'etat du robot : la ligne TikTok seulement ; absent des autres reseaux. */
  robot: robotLigneSchema.nullable().optional(),
});
export type LigneReseau = z.infer<typeof ligneReseauSchema>;

/**
 * La `raison` d'un reseau qu'aucune destination ne sert encore (les cinq des lots a venir). Le backend l'ecrit,
 * la regie la reconnait : c'est l'etat attendu de ces reseaux, pas un geste a faire, donc elle ne le montre pas
 * en rouge comme un reseau qui devrait publier et ne le peut pas (variables manquantes, identifiants refuses).
 * Ecrite ici pour que les deux cotes ne puissent pas la changer l'un sans l'autre.
 */
export const RAISON_PAS_ENCORE_BRANCHE = "pas encore branché";

/**
 * La `raison` d'un reseau de Meta dont le compte est autorise mais qui n'a pas encore de destination (la
 * publication arrive avec la suite du lot M1). Son etat attendu, comme `RAISON_PAS_ENCORE_BRANCHE` : la regie ne
 * la montre pas en rouge. La ligne reste non branchee (`branche` faux) : rien ne part tant qu'aucune destination
 * n'existe.
 */
export const RAISON_AUTORISE_SANS_PUBLICATION =
  "autorisé, la publication arrive avec la suite du lot M1";

/** La reponse de `GET /api/diffusion/reseaux` : les sept reseaux, dans l'ordre de `RESEAUX`. */
export const reponseReseauxSchema = z.object({ reseaux: z.array(ligneReseauSchema) });
export type ReponseReseaux = z.infer<typeof reponseReseauxSchema>;

/**
 * Les reseaux dont Castellan sait obtenir l'autorisation de son compte, par la paire de routes
 * `GET /api/diffusion/reseaux/{reseau}/autoriser` et `/retour`. YouTube dans ce lot, les autres a mesure que
 * leur autorisation est ecrite (TikTok, puis les trois de Meta).
 */
export const RESEAUX_AUTORISABLES = [
  "youtube",
  "instagram",
  "facebook",
  "threads",
  "tiktok",
] as const;
export type ReseauAutorisable = (typeof RESEAUX_AUTORISABLES)[number];

/**
 * Les codes que `GET /api/diffusion/reseaux/{reseau}/retour` met dans l'adresse de la regie
 * (`/regie/diffusion?onglet=reseaux&<reseau>=<code>`). Une liste fermee, ecrite ici : jamais un morceau
 * de ce que le reseau a repondu.
 * - `ok` : le compte est autorise, ses jetons sont enregistres ;
 * - `refus` : la personne a refuse l'autorisation chez le reseau (tout parametre `error`) ;
 * - `etat_invalide` : etat absent, inconnu, deja consomme, trop vieux, ou cree pour un autre reseau ;
 * - `code_refuse` : le reseau a refuse l'echange du code, ou le consentement est incomplet (pas de jeton
 *   de rafraichissement, droit d'envoi decoche, compte sans chaine). Un identifiant de client ou une
 *   adresse de retour mal regles dans la console du reseau donnent aussi ce code ;
 * - `indisponible` : le reseau, la base ou la configuration ne permettent pas de conclure ; on refait.
 */
export const RETOURS_AUTORISATION = [
  "ok",
  "refus",
  "etat_invalide",
  "code_refuse",
  "indisponible",
] as const;
export type RetourAutorisation = (typeof RETOURS_AUTORISATION)[number];
/** Les noms d'avant les autres reseaux : la meme liste, gardee pour ce qui l'importe encore. */
export const RETOURS_YOUTUBE = RETOURS_AUTORISATION;
export type RetourYoutube = RetourAutorisation;

/**
 * Les codes d'une erreur de l'API de TikTok (`ErreurTiktok`, `tiktok.ts`) : la lecture, l'autorisation et l'envoi
 * en brouillon. Ce ne sont pas des causes d'envoi (l'horloge ne publie pas sur TikTok dans ce lot) : ils n'entrent pas
 * dans `CODES_CAUSE`. Ecrits par l'app, jamais un mot de TikTok.
 */
export const CODES_TIKTOK = [
  "tiktok_non_configure",
  "tiktok_non_autorise",
  "tiktok_a_reautoriser",
  "tiktok_portee_manquante",
  "tiktok_jetons_indisponibles",
  "tiktok_injoignable",
  "tiktok_brouillon_incertain",
  "tiktok_brouillon_a_valider",
  "tiktok_5xx",
  "tiktok_limite",
  "tiktok_refus",
  "tiktok_reponse_invalide",
  "tiktok_brouillons_en_attente",
  "tiktok_envoi_expire",
  "tiktok_fichier_trop_lourd",
  "tiktok_fichier_absent",
] as const;
export type CodeTiktok = (typeof CODES_TIKTOK)[number];

/**
 * Ce que l'essai de la lecture de TikTok rend a la regie (`GET /api/diffusion/reseaux/tiktok/lecture`) : le compte,
 * ses chiffres et ses vingt dernieres videos. Les identifiants restent des chaines, les dates sont en ISO 8601. Rien
 * n'est garde : la reponse va a la regie, qui la montre pour que Nicolas la compare a l'appli TikTok.
 */
export const videoTiktokSchema = z.object({
  id: z.string(),
  date: z.string(),
  titre: z.string(),
  vues: z.number().int().nonnegative(),
  likes: z.number().int().nonnegative(),
  commentaires: z.number().int().nonnegative(),
  partages: z.number().int().nonnegative(),
  lien: z.string(),
});
export type VideoTiktok = z.infer<typeof videoTiktokSchema>;
export const lectureTiktokSchema = z.object({
  compte: z.string(),
  abonnes: z.number().int().nonnegative(),
  abonnements: z.number().int().nonnegative(),
  likes: z.number().int().nonnegative(),
  nombreVideos: z.number().int().nonnegative(),
  videos: z.array(videoTiktokSchema),
});
export type LectureTiktok = z.infer<typeof lectureTiktokSchema>;

/**
 * L'essai du brouillon de TikTok : la video part dans la boite de reception de TikTok, la personne la poste
 * elle-meme. `statut` suit l'envoi : `envoi` (TikTok recoit ou traite), `dans_la_boite` (la notification est partie),
 * `publie` (la personne a poste), `echec` (avec une `raison` ecrite par l'app, jamais un mot de TikTok).
 */
export const STATUTS_BROUILLON_TIKTOK = ["envoi", "dans_la_boite", "publie", "echec"] as const;
export const brouillonTiktokSchema = z.object({
  publishId: z.string(),
  statut: z.enum(STATUTS_BROUILLON_TIKTOK),
  raison: z.string().nullable(),
});
export type BrouillonTiktok = z.infer<typeof brouillonTiktokSchema>;
/**
 * Le dernier brouillon envoye a TikTok, garde par le serveur (`GET /api/diffusion/reseaux/tiktok/brouillon`) pour que
 * la regie le retrouve apres un rechargement de la page. `clipId` est le clip du stock, `envoyeLe` l'instant de l'envoi,
 * `relueLe` celui de la derniere relecture de l'etat (`null` tant qu'il n'a pas ete relu). `dernier` vaut `null`
 * tant qu'aucun brouillon n'est parti.
 */
export const dernierBrouillonTiktokSchema = brouillonTiktokSchema.extend({
  clipId: z.string(),
  envoyeLe: z.string(),
  relueLe: z.string().nullable(),
});
export type DernierBrouillonTiktok = z.infer<typeof dernierBrouillonTiktokSchema>;
export const reponseDernierBrouillonTiktokSchema = z.object({
  dernier: dernierBrouillonTiktokSchema.nullable(),
});
export type ReponseDernierBrouillonTiktok = z.infer<typeof reponseDernierBrouillonTiktokSchema>;
/** Un identifiant d'envoi de TikTok : 64 caracteres au plus, ceux que TikTok emploie. */
export const PUBLISH_ID_TIKTOK = /^[A-Za-z0-9._~-]{1,64}$/;
/** Le corps de l'envoi d'un brouillon : le clip du stock, par son identifiant. */
export const envoiBrouillonTiktokSchema = z.object({
  clipId: z.string().regex(/^[1-9][0-9]{0,17}$/),
});

/** Les etats d'un envoi, ceux de la contrainte de `reseaux_envois.etat` (`migrations/1005_reseaux.sql`). */
export const ETATS_ENVOI = [
  "prevu",
  "en_cours",
  "programme",
  "parti",
  "echec",
  "manque",
  "a_verifier",
  "retire",
] as const;
export type EtatEnvoi = (typeof ETATS_ENVOI)[number];
export const etatEnvoiSchema = z.enum(ETATS_ENVOI);

/** Ce que l'envoi publie : une video (avec son fichier), un post de texte ou une image. */
export const TYPES_ENVOI = ["video", "texte", "image"] as const;
export const typeEnvoiSchema = z.enum(TYPES_ENVOI);

/**
 * Un envoi de l'horloge, tel que `GET /api/diffusion/envois` le rend (la ligne de `reseaux_envois`).
 *
 * - `moment` : l'heure prevue de la publication (date ISO 8601, UTC) ;
 * - `etat` : voir `ETATS_ENVOI`. Seuls `echec`, `manque` et `a_verifier` se relancent. Un envoi `a_verifier` que
 *   Nicolas a trouve sur le reseau se ferme par `POST /api/diffusion/envois/{id}/parti` (« C'est parti ») : il passe
 *   a `parti`, **garde sa cause** et n'a pas d'`idExterne` (personne ne l'a rendu). Seuls `prevu`, `echec`, `manque`
 *   et `a_verifier` se retirent par `POST /api/diffusion/envois/{id}/retirer` (« Retirer ») : l'envoi passe a `retire`,
 *   ne part jamais, et rien ne le remet a `prevu` ; le post reste dans sa semaine ;
 * - `cause` : un code de `CODES_CAUSE` pour un echec ou une issue incertaine, `null` pour un envoi qui
 *   n'a rien eu. Le passage a « manque » (plus d'une heure de retard) et celui a « a_verifier » (plus de
 *   30 minutes « en cours ») n'ecrivent pas de cause, mais ne vident pas celle qui est la : `echouer` en
 *   ecrit une en remettant un echec reessayable a « prevu », et ni la reservation ni ces deux passages ne
 *   l'effacent (seuls `conclure` et `relancer` le font). Un envoi « manque » ou « a_verifier » peut donc
 *   porter la cause d'un essai precedent (`youtube_5xx`), qui ne dit rien de son etat d'aujourd'hui : la
 *   regie dit toujours la meme chose d'un envoi manque, et ne reprend la phrase d'une cause pour un envoi a
 *   verifier que si c'est une issue incertaine. C'est du texte de la base : la regie confronte le code a sa
 *   liste et ecrit sa propre phrase, un code qu'elle ne connait pas ne casse rien (`diffusion-texts.ts`) ;
 * - `idExterne` : l'identifiant que le reseau a donne a la publication, une fois qu'elle existe.
 */
export const envoiSchema = z.object({
  id: z.string(),
  cle: z.string(),
  reseau: reseauSchema,
  type: typeEnvoiSchema,
  voie: voieSchema,
  moment: z.string(),
  titre: z.string().nullable(),
  texte: z.string(),
  fichierId: z.string().nullable(),
  /** Ce qui est propre a un reseau (tags, texte alternatif...) : un objet JSON, `{}` par defaut. */
  extra: z.record(z.string(), z.unknown()).default({}),
  etat: etatEnvoiSchema,
  essais: z.number().int().nonnegative(),
  idExterne: z.string().nullable(),
  cause: z.string().nullable(),
});
export type EnvoiContrat = z.infer<typeof envoiSchema>;

/**
 * La demande d'un essai d'envoi (`POST /api/diffusion/reseaux/{reseau}/essai`) : un vrai post, a une heure choisie.
 * `clipId` est une video du stock dont le fichier est complet ; sans lui, un texte seul (Bluesky, Threads). `moment`
 * est au moins trente minutes dans l'avenir et au plus sept jours (la fenetre de Facebook), ce que le serveur juge a son
 * horloge. Un texte seul est refuse pour un reseau qui ne publie que de la video (YouTube, Facebook, Instagram).
 * `uuid` est tire une fois par le formulaire a son ouverture : la cle de l'envoi en vient, et un second envoi du meme
 * uuid ne cree pas un second envoi.
 */
export const demandeEssaiSchema = z.object({
  uuid: z.uuid(),
  clipId: z
    .string()
    .regex(/^[1-9][0-9]{0,17}$/)
    .optional(),
  texte: z.string().min(1).max(5000),
  titre: z.string().max(100).optional(),
  moment: z.iso.datetime({ offset: true }),
});
export type DemandeEssai = z.infer<typeof demandeEssaiSchema>;

/** La reponse d'un essai : l'envoi cree, tel que le suivi le montrera. */
export const reponseEssaiSchema = z.object({ envoi: envoiSchema });
export type ReponseEssai = z.infer<typeof reponseEssaiSchema>;

/** La reponse de `GET /api/diffusion/envois` : les envois de l'intervalle, du plus ancien au plus recent. */
export const reponseEnvoisSchema = z.object({ envois: z.array(envoiSchema) });
export type ReponseEnvois = z.infer<typeof reponseEnvoisSchema>;

/**
 * La plus longue fenetre que `GET /api/diffusion/envois` rend, en jours, bornes comprises. La regie en
 * demande exactement 14 : sept jours avant maintenant et sept jours apres.
 */
export const FENETRE_ENVOIS_JOURS_MAX = 14;

/**
 * Les codes de cause qu'un envoi porte (`Envoi.cause`) : tout ce que l'app ecrit quand un envoi echoue
 * ou que son issue est incertaine. **Chacun a une phrase dans la regie** (`diffusion-texts.ts`,
 * `Record<CodeCause, string>` : un code ajoute ici sans sa phrase ne compile pas). Un test du backend
 * (`reseaux/codes-cause.test.ts`) lit les sources du module reseaux et fait echouer toute liste qui ne
 * porte pas un code qu'elles peuvent ecrire, ou qui en garde un qu'aucune n'ecrit.
 *
 * - l'horloge (`horloge.ts`) : `fichier_absent` (le fichier de la video manquait au depart),
 *   `erreur_interne` (une erreur de l'app, avant ou pendant l'appel au reseau), `reseau_non_branche`,
 *   `resultat_invalide` (le reseau a repondu, pas comme le contrat le veut) ;
 * - le constructeur d'une erreur de reseau (`destination.ts`) : `code_invalide` et `operation_invalide`,
 *   qui remplacent un code ou une operation mal formes ;
 * - YouTube (`youtube.ts`) et Bluesky (`bluesky.ts`) : un code court `<reseau>_<mot>` par cause. Les
 *   reseaux des lots suivants ajoutent les leurs ici.
 *
 * `bluesky_401` est le refus certain du serveur du compte (apres un renouvellement de la session) ;
 * `bluesky_video_401` celui du service video (`video.bsky.app`), qui se retente.
 */
export const CODES_CAUSE = [
  "fichier_absent",
  "erreur_interne",
  "reseau_non_branche",
  "resultat_invalide",
  "code_invalide",
  "operation_invalide",
  "youtube_non_configure",
  "youtube_envoi_invalide",
  "youtube_a_reautoriser",
  "youtube_jetons_indisponibles",
  "youtube_injoignable",
  "youtube_5xx",
  "youtube_reponse_invalide",
  "youtube_401",
  "youtube_quota",
  "youtube_refus",
  "youtube_envoi_incomplet",
  "youtube_issue_inconnue",
  "bluesky_non_configure",
  "bluesky_identifiants_refuses",
  "bluesky_401",
  "bluesky_video_401",
  "bluesky_limite",
  "bluesky_injoignable",
  "bluesky_5xx",
  "bluesky_refus",
  "bluesky_reponse_invalide",
  "bluesky_quota",
  "bluesky_video_refusee",
  "bluesky_video_en_attente",
  "bluesky_video_trop_lourde",
  "bluesky_image_refusee",
  "bluesky_image_trop_lourde",
  "bluesky_texte_trop_long",
  "bluesky_envoi_invalide",
  "bluesky_issue_inconnue",
  "facebook_non_configure",
  "facebook_envoi_invalide",
  "facebook_a_reautoriser",
  "facebook_jetons_indisponibles",
  "facebook_injoignable",
  "facebook_5xx",
  "facebook_reponse_invalide",
  "facebook_envoi_incomplet",
  "facebook_limite",
  "facebook_permission",
  "facebook_bloque",
  "facebook_fichier_refuse",
  "facebook_refus",
  "facebook_issue_inconnue",
  "instagram_non_configure",
  "instagram_envoi_invalide",
  "instagram_texte_trop_long",
  "instagram_a_reautoriser",
  "instagram_jetons_indisponibles",
  "instagram_media_indisponible",
  "instagram_injoignable",
  "instagram_5xx",
  "instagram_reponse_invalide",
  "instagram_en_traitement",
  "instagram_limite",
  "instagram_quota",
  "instagram_conteneur_refuse",
  "instagram_conteneur_perime",
  "instagram_permission",
  "instagram_bloque",
  "instagram_compte_restreint",
  "instagram_fichier_refuse",
  "instagram_refus",
  "instagram_issue_inconnue",
  "threads_non_configure",
  "threads_envoi_invalide",
  "threads_texte_trop_long",
  "threads_trop_de_liens",
  "threads_alt_trop_long",
  "threads_a_reautoriser",
  "threads_jetons_indisponibles",
  "threads_media_indisponible",
  "threads_injoignable",
  "threads_5xx",
  "threads_reponse_invalide",
  "threads_en_traitement",
  "threads_limite",
  "threads_quota",
  "threads_conteneur_refuse",
  "threads_conteneur_perime",
  "threads_refus",
  "threads_issue_inconnue",
  "media_introuvable",
] as const;
export type CodeCause = (typeof CODES_CAUSE)[number];

/**
 * Parmi les codes de cause, ceux qui disent que le contenu est peut-etre parti : l'app ne sait pas ce
 * que le reseau a fait. Un envoi qui en porte un (et tout envoi « a verifier ») se verifie sur le reseau
 * AVANT d'etre relance : relancer le publierait une seconde fois. La phrase de chacun le dit.
 */
export const CODES_CAUSE_INCERTAINS = [
  "erreur_interne",
  "resultat_invalide",
  "code_invalide",
  "operation_invalide",
  "youtube_issue_inconnue",
  "bluesky_issue_inconnue",
  "facebook_issue_inconnue",
  "instagram_issue_inconnue",
  "threads_issue_inconnue",
] as const satisfies readonly CodeCause[];
